// Kitchen printers (ops parity api-commerce/printing.md §1; register hardware.md §6). The API never prints: it holds
// the printers a location has, and the catalog says which items go to which printer (category `printerIds`, product
// `includedPrinterIds` / `excludedPrinterIds`). The register renders and sends the tickets (`@radish/receipts`,
// `@radish/core/kitchen`). Printers are location config, like the catalog's supporting lists.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { LocationId, PrinterId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { CatalogEntityNotFound, CatalogInvalid } from './errors.ts';

/** Legacy printer `type`, named for its axis: `kds` is a KDS screen, `label` a Brother label printer. */
export const PrinterKind = z.enum(['thermal', 'impact', 'usb', 'kds', 'label']).meta({ id: 'PrinterKind' });

export const PrinterFields = z
  .object({
    name: z.string().trim().min(1).max(255),
    /** Required unless the kind is usb, kds or label (legacy PrinterRequest): the printer's address on the network. */
    ip: z.string().trim().min(1).max(64).nullable(),
    kind: PrinterKind,
    /** Every item gets its own ticket on this printer. */
    itemize: z.boolean(),
    /** Misc items start with this printer selected (legacy `preselect_on_misc_items`). */
    preselectOnMiscItems: z.boolean(),
  })
  .meta({ id: 'PrinterFields' });

export const PrinterView = PrinterFields.extend({ printerId: PrinterId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'PrinterView' });

const configErrors = [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound] as const;
const common = { availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: configErrors, dryRun: true } as const;

export const UpsertPrinter = defineCommand({
  ...common,
  name: 'catalog.upsertPrinter',
  summary: 'Add a kitchen, label or KDS printer, or change one (name, IP address, kind, itemize, misc-item preselect).',
  input: z.object({ printerId: PrinterId, locationId: LocationId, changes: PrinterFields.partial() }),
  output: PrinterView,
  permission: 'hardware.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/printers', 'PUT /v1/printers/{id}'],
});

export const DeletePrinter = defineCommand({
  ...common,
  name: 'catalog.deletePrinter',
  summary: 'Delete a printer; the categories and items routed to it stop printing there.',
  input: z.object({ printerId: PrinterId, locationId: LocationId }),
  output: z.object({ id: PrinterId, deleted: z.literal(true) }),
  permission: 'hardware.manage',
  risk: 'medium',
  agent: { expose: false },
  legacy: ['DELETE /v1/printers/{id}'],
});

export const ListPrinters = defineQuery({
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  permission: { session: 'user' },
  name: 'catalog.printers.list',
  summary: "The location's printers of every kind.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ printers: z.array(PrinterView) }),
  legacy: ['GET /v1/printers'],
});

export const printerCommands = [UpsertPrinter, DeletePrinter] as const;
export const printerQueries = [ListPrinters] as const;
